import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { canonicalizeMacBundleId, canonicalizeWinProcessName, platformCoverage, validateDraft } from './denyListUtils'

import styles from './AddAppDenyEntryModal.module.scss'

import Button from '@/components/Button'
import ConfirmDialog from '@/components/ConfirmDialog'
import Dialog from '@/components/Dialog'
import Input from '@/components/Input'
import { useToast } from '@/components/Toast/ToastContext'
import Tooltip from '@/components/Tooltip'
import { AppDispatch } from '@/store'
import { updateAppDenyListThunk } from '@/store/features/user/userSlice'
import { AppDenyEntry, SettingType } from '@/types'

interface AddAppDenyEntryModalProps {
  isOpen: boolean
  close: () => void
  currentList: AppDenyEntry[]
  isAdmin: boolean
  authoringEnabled?: boolean
}

type Phase = 'editing' | 'saving'

const buildEntry = (displayName: string, macBundleId: string, winProcessName: string): AppDenyEntry => {
  if (macBundleId && winProcessName) return { displayName, macBundleId, winProcessName }
  if (macBundleId) return { displayName, macBundleId }
  return { displayName, winProcessName }
}

const normalizeEntry = (entry: AppDenyEntry): AppDenyEntry =>
  buildEntry(
    entry.displayName.trim(),
    canonicalizeMacBundleId(entry.macBundleId),
    canonicalizeWinProcessName(entry.winProcessName)
  )

const entriesEqual = (a: AppDenyEntry, b: AppDenyEntry): boolean =>
  a.displayName.trim() === b.displayName.trim() &&
  canonicalizeMacBundleId(a.macBundleId) === canonicalizeMacBundleId(b.macBundleId) &&
  canonicalizeWinProcessName(a.winProcessName) === canonicalizeWinProcessName(b.winProcessName)

const listsEqual = (a: AppDenyEntry[], b: AppDenyEntry[]): boolean =>
  a.length === b.length && a.every((entry, index) => entriesEqual(entry, b[index]))

const applyEntryToList = (list: AppDenyEntry[], entry: AppDenyEntry, editIndex: number | null): AppDenyEntry[] =>
  editIndex === null ? [...list, entry] : list.map((existing, index) => (index === editIndex ? entry : existing))

const AddAppDenyEntryModal = ({
  isOpen,
  close,
  currentList,
  isAdmin,
  authoringEnabled = true,
}: AddAppDenyEntryModalProps) => {
  const dispatch = useDispatch<AppDispatch>()
  const { updateToast } = useToast()

  const initialList = useMemo(() => currentList.map(normalizeEntry), [currentList])

  const [draftList, setDraftList] = useState<AppDenyEntry[]>(initialList)
  const [displayName, setDisplayName] = useState('')
  const [macBundleId, setMacBundleId] = useState('')
  const [winProcessName, setWinProcessName] = useState('')
  const [editIndex, setEditIndex] = useState<number | null>(null)
  const [phase, setPhase] = useState<Phase>('editing')
  const [apiError, setApiError] = useState<string | null>(null)
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const wasOpenRef = useRef(false)

  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current
    wasOpenRef.current = isOpen
    if (!justOpened) return

    setDraftList(initialList)
    setDisplayName('')
    setMacBundleId('')
    setWinProcessName('')
    setEditIndex(null)
    setPhase('editing')
    setApiError(null)
    setShowDiscardConfirm(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const { entry, error: validationError } = validateDraft({ displayName, macBundleId, winProcessName })
  const hasPendingDraft = Boolean(displayName.trim() || macBundleId.trim() || winProcessName.trim())
  const saveList =
    hasPendingDraft && entry && authoringEnabled ? applyEntryToList(draftList, entry, editIndex) : draftList
  const isListDirty = !listsEqual(draftList, initialList)
  const hasSaveChanges = !listsEqual(saveList, initialList)

  const clearForm = () => {
    setDisplayName('')
    setMacBundleId('')
    setWinProcessName('')
    setEditIndex(null)
  }

  const onField = (setter: (v: string) => void) => (v: string) => {
    if (apiError) setApiError(null)
    setter(v)
  }

  const startEdit = (index: number) => {
    const entryToEdit = draftList[index]
    setDisplayName(entryToEdit.displayName)
    setMacBundleId(entryToEdit.macBundleId ?? '')
    setWinProcessName(entryToEdit.winProcessName ?? '')
    setEditIndex(index)
    setApiError(null)
  }

  const addOrUpdateEntry = () => {
    if (!entry) return
    setDraftList((list) => applyEntryToList(list, entry, editIndex))
    clearForm()
  }

  const removeEntry = (index: number) => {
    setDraftList((list) => list.filter((_, i) => i !== index))
    if (editIndex === index) clearForm()
    else if (editIndex !== null && editIndex > index) setEditIndex(editIndex - 1)
  }

  const doSave = async () => {
    setPhase('saving')
    setApiError(null)
    try {
      await dispatch(updateAppDenyListThunk({ settings: { [SettingType.appDenyList]: saveList } })).unwrap()
      updateToast('Blocked apps updated', 'success')
      close()
    } catch (error) {
      setApiError(typeof error === 'string' ? error : 'Failed to save. Please try again.')
      setPhase('editing')
    }
  }

  const handleClose = () => {
    if (phase === 'saving') return
    if (isListDirty || displayName || macBundleId || winProcessName) {
      setShowDiscardConfirm(true)
      return
    }
    close()
  }

  const handleDiscard = () => {
    setShowDiscardConfirm(false)
    close()
  }

  const addBlockedReason = (() => {
    if (!isAdmin) return 'Only admins can edit blocked apps.'
    if (!authoringEnabled) return 'Adding and editing apps is not enabled for this organization.'
    if (validationError) return validationError
    return null
  })()

  const saveBlockedReason = (() => {
    if (!isAdmin) return 'Only admins can edit blocked apps.'
    if (hasPendingDraft && !authoringEnabled) return 'Adding and editing apps is not enabled for this organization.'
    if (hasPendingDraft && validationError) return validationError
    if (!hasSaveChanges) return 'No changes to save.'
    return null
  })()

  const blockedStatusMessage = (() => {
    if (apiError) return null
    if (saveBlockedReason && saveBlockedReason !== 'No changes to save.') return saveBlockedReason
    if (addBlockedReason && (hasPendingDraft || !isAdmin || !authoringEnabled)) return addBlockedReason
    return null
  })()

  const addButton = (
    <Button
      title={editIndex === null ? 'Add app' : 'Update app'}
      onClick={addOrUpdateEntry}
      type="dark"
      isDisabled={addBlockedReason !== null || phase === 'saving'}
    />
  )

  const saveButton = (
    <Button
      title="Save"
      onClick={() => void doSave()}
      type="dark"
      isDisabled={saveBlockedReason !== null || phase === 'saving'}
      isLoading={phase === 'saving'}
    />
  )

  return (
    <>
      <Dialog
        isOpen={isOpen && !showDiscardConfirm}
        title="Edit blocked apps"
        close={handleClose}
        minWidth="min(640px, calc(100vw - 64px))"
      >
        <div className={styles.modalBodyWide}>
          <p className={styles.helpText}>
            Voice-to-text turns off when a blocked app is in focus. Add a macOS bundle ID, a Windows process name, or
            both.
          </p>

          {draftList.length === 0 ? (
            <p className={styles.emptyState}>No apps are blocked.</p>
          ) : (
            <ul className={styles.entryList}>
              {draftList.map((entry, index) => (
                <li key={`${entry.displayName}-${index}`} className={styles.entryRow}>
                  <div className={styles.entryMain}>
                    <span className={styles.entryName}>{entry.displayName}</span>
                    <div className={styles.entryIdentifiers}>
                      {entry.macBundleId && <code>{entry.macBundleId}</code>}
                      {entry.winProcessName && <code>{entry.winProcessName}</code>}
                    </div>
                  </div>
                  <span className={styles.platformBadge}>{platformCoverage(entry)}</span>
                  <div className={styles.rowActions}>
                    <Button
                      title="Edit"
                      ariaLabel={`Edit ${entry.displayName}`}
                      onClick={() => startEdit(index)}
                      type="light"
                      isDisabled={!isAdmin || !authoringEnabled || phase === 'saving'}
                    />
                    <Button
                      title="Remove"
                      ariaLabel={`Remove ${entry.displayName}`}
                      onClick={() => removeEntry(index)}
                      type="destructiveLight"
                      isDisabled={!isAdmin || phase === 'saving'}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className={styles.addEntryPanel}>
            <div className={styles.field}>
              <label className={styles.fieldLabel} htmlFor="app-deny-display-name">
                Display name
              </label>
              <Input
                id="app-deny-display-name"
                type="text"
                value={displayName}
                onInput={onField(setDisplayName)}
                placeholder="e.g. 1Password"
                isDisabled={phase === 'saving' || !isAdmin || !authoringEnabled}
              />
            </div>

            <div className={styles.fieldGrid}>
              <div className={styles.field}>
                <label className={styles.fieldLabel} htmlFor="app-deny-mac">
                  macOS bundle ID
                </label>
                <Input
                  id="app-deny-mac"
                  type="text"
                  value={macBundleId}
                  onInput={onField(setMacBundleId)}
                  placeholder="e.g. com.1password.1password7"
                  isDisabled={phase === 'saving' || !isAdmin || !authoringEnabled}
                  className={styles.input}
                />
              </div>

              <div className={styles.field}>
                <label className={styles.fieldLabel} htmlFor="app-deny-win">
                  Windows process name
                </label>
                <Input
                  id="app-deny-win"
                  type="text"
                  value={winProcessName}
                  onInput={onField(setWinProcessName)}
                  placeholder="e.g. 1password.exe"
                  isDisabled={phase === 'saving' || !isAdmin || !authoringEnabled}
                  className={styles.input}
                />
              </div>
            </div>

            <div className={styles.addEntryActions}>
              {editIndex !== null && (
                <Button title="Cancel edit" onClick={clearForm} type="light" isDisabled={phase === 'saving'} />
              )}
              {addBlockedReason !== null ? (
                <Tooltip text={addBlockedReason} placement="top" hideArrow>
                  {addButton}
                </Tooltip>
              ) : (
                addButton
              )}
            </div>
          </div>

          <div className={styles.errorSlot}>
            {apiError && (
              <p className={styles.errorText} role="alert">
                {apiError}
              </p>
            )}
            {blockedStatusMessage && (
              <p className={styles.statusText} role="status">
                {blockedStatusMessage}
              </p>
            )}
          </div>

          <div className={styles.modalActions}>
            <Button title="Cancel" onClick={handleClose} type="light" isDisabled={phase === 'saving'} />
            {saveBlockedReason !== null ? (
              <Tooltip text={saveBlockedReason} placement="top" hideArrow>
                {saveButton}
              </Tooltip>
            ) : (
              saveButton
            )}
          </div>
        </div>
      </Dialog>

      <ConfirmDialog
        isOpen={showDiscardConfirm}
        title="Discard unsaved changes?"
        message="Your changes to blocked apps haven't been saved. If you discard now, you'll lose them."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        confirmType="destructive"
        onConfirm={handleDiscard}
        onCancel={() => setShowDiscardConfirm(false)}
      />
    </>
  )
}

export default AddAppDenyEntryModal
